import { divide } from 'modules/math';
import navigationManager from 'modules/shop/navigationManager';
import { CustomCategoriesNames } from 'modules/shop/navigationManager/types';
import {
  BasketResponse,
  CartItem,
} from 'commerce/scayle/user-interaction/types';
import { Product } from 'commerce/scayle/utils/adapter';
import { CustomDataType, GTMProduct, GTMProductParsed } from '../modules/types';

type ProductCategoriesType = { categoryName: string }[] | undefined;

export const getProductCategories = (product: Product) => {
  const storageParentCategories = navigationManager.getStorageCategories();
  const referenceKey = product.referenceKey;
  const categories = product.categories;

  if (storageParentCategories?.[referenceKey]) {
    // get categories which was added from specific category page, home page or search result page
    const categoryName = storageParentCategories[referenceKey];

    // get category for product which was added to cart from search result page
    if (categoryName === CustomCategoriesNames.searchResultCategory) {
      return [{ categoryName: CustomCategoriesNames.searchResultCategory }];
    }

    // get category for product which was added to cart from home page
    if (categoryName === CustomCategoriesNames.homeCategory) {
      return [{ categoryName: CustomCategoriesNames.homeCategory }];
    }

    // get categories which were added from specific category page
    return getProductCategoryNames(
      categories as unknown as ProductCategoriesType[],
      categoryName,
    );
  } else {
    //get categories from product data categories array
    return Object.values(categories ?? {})
      .find((subCategories) => subCategories.length >= 2)
      ?.map((item) => ({
        categoryName: item.categoryName,
      }));
  }
};

export const parseCartItemToGTMProduct = (
  item: CartItem,
  customData?: CustomDataType,
): GTMProductParsed => {
  const { quantity, variant, product: productItem } = item;
  const itemPrice = item.price;
  const product = Product.createProduct(productItem);

  const price = customData?.price || itemPrice.total.withTax;
  const itemVariant = product.color.value;
  const priceValue = price / 100;
  const categories = getProductCategories(product);

  return {
    item_id: variant.referenceKey || '',
    item_name: product.name || '',
    item_brand: product.brand || '',
    item_category: categories?.[0]?.categoryName || '',
    item_category2: categories?.[1]?.categoryName || '',
    item_category3: !!priceValue ? 'on sale' : 'for free',
    item_category4: itemVariant || '',
    item_variant: itemVariant || '',
    discount: customData?.discount || 0,
    quantity: customData?.quantity || quantity || 0,
    price: priceValue || 0,
  };
};

export const parseDataToGTMCart = (
  cart: BasketResponse | null,
): {
  items: GTMProductParsed[];
  currency: string;
  shippingTier: string;
} | null => {
  return !cart
    ? null
    : {
        items: cart?.items?.map((item) => parseCartItemToGTMProduct(item)),
        currency: cart?.cost?.currencyCode || '',
        shippingTier: cart?.packages?.[0]?.carrierKey || '',
      };
};

export const parseProductToGTMProduct = (
  product: GTMProduct,
  categories: ProductCategoriesType,
  customData?: CustomDataType,
): GTMProductParsed => {
  const id =
    customData?.productReferenceKey || product.variants?.[0].referenceKey;
  const price = customData?.price || product.price.withTax;
  const priceValue = divide(price, 100);

  return {
    item_name: product.name,
    item_id: id || '',
    price: priceValue || 0,
    discount: customData?.discount || 0,
    item_brand: product.brand || '',
    item_category: categories?.[0]?.categoryName || '',
    item_category2: categories?.[1]?.categoryName || '',
    item_category3: !!price ? 'on sale' : 'for free',
    item_category4: product.color.value || '',
    item_variant: product.color.value || '',
    quantity: 1,
  };
};

export const parseDataToGTMItemViewDetail = (
  product: GTMProduct,
): { currency: string; item: GTMProductParsed } => {
  return {
    currency: product.price.currencyCode || '',
    item: parseProductToGTMProduct(product, [
      { categoryName: product.category || '' },
      { categoryName: product.seoUrl },
    ]),
  };
};

export const parseDataToGTMItemListView = (
  products: GTMProduct[],
): { currency: string; items: GTMProductParsed[] } => {
  return {
    currency: products[0]?.price?.currencyCode || '',
    items: products.map((product) =>
      parseProductToGTMProduct(product, [
        { categoryName: product.category || '' },
        { categoryName: product.seoUrl },
      ]),
    ),
  };
};

/** get category names from product data categories array */
const getProductCategoryNames = (
  categories: ProductCategoriesType[],
  targetCategoryName: string,
) => {
  for (const subCategories of Object.values(categories)) {
    if (!subCategories) {
      return [];
    }

    for (const category of subCategories) {
      if (category.categoryName === targetCategoryName) {
        return subCategories.map((cat) => ({
          categoryName: cat.categoryName,
        }));
      }
    }
  }
  return [];
};
